/**
 * TeamAlignmentHomeCard.jsx
 *
 * A persistent HomePage card that opens the Team Alignment hub in
 * one tap. Unlike `TeamAlignmentBanner` (which only glows for an
 * unwatched transmission), this card is *always* visible to anyone
 * who belongs to a team — so the leader can step in and walk the
 * day's ritual at any time.
 *
 * Loads `GET /api/team-alignment/teams/mine` once on mount. If the
 * user has 0 teams, returns null and lets the Partner Pack flow
 * surface a "Start your Circle" CTA elsewhere.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, ArrowRight, Sparkles, Eye } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeamAlignmentHomeCard() {
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [count, setCount] = useState(0);
  // D3.59 — today's carried line ("Carry one with me today"). Null until
  // fetched. When present, surfaces as a soft italic compass below the
  // team name. Auto-expires server-side at midnight UTC.
  const [carried, setCarried] = useState(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("reclaim_token");
    if (!token) return undefined;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/team-alignment/teams/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const list = r.data?.items || [];
        if (list.length) { setTeam(list[0]); setCount(list.length); }
      } catch {
        /* silent */
      } finally {
        if (!cancelled) setResolved(true);
      }
      // Carried line fetch — independent of team list so it surfaces
      // even if the user's team data is stale.
      try {
        const c = await axios.get(`${API}/api/team-alignment/my/carried`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && c.data?.carried) setCarried(c.data.carried);
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Until the fetch resolves, don't render anything (avoids a flash
  // of the "no team" CTA for users who actually have a team).
  if (!resolved) return null;

  // ── No team yet — surface a quiet card that routes to the hub
  // so the user can join a circle, see an invite code prompt, or
  // start their own (Partner perk). The Team Alignment hub already
  // handles all three paths inside `Step into the field`. ──
  if (!team) {
    return (
      <motion.button
        type="button"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onClick={() => navigate("/team-alignment")}
        className="w-full rounded-2xl p-5 sm:p-6 mb-6 text-left flex items-center gap-4"
        style={{
          background:
            "radial-gradient(ellipse at 0% 50%, rgba(215,187,122,0.16) 0%, rgba(7,13,11,0.92) 70%)",
          border: "1px dashed rgba(215,187,122,0.45)",
        }}
        data-testid="home-team-alignment-card-skeleton"
      >
        <motion.div
          animate={{ scale: [1, 1.05, 1], opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
          style={{
            background: "rgba(215,187,122,0.16)",
            border: "1px solid rgba(215,187,122,0.45)",
          }}
        >
          <Sparkles size={14} style={{ color: "#F3EDDD" }} />
        </motion.div>
        <div className="flex-1 min-w-0">
          <p
            className="uppercase tracking-[0.32em] mb-1"
            style={{ color: "#D7BB7A", fontFamily: '"Manrope", sans-serif', fontSize: "0.56rem" }}
          >
            Team Alignment
          </p>
          <p
            className="italic mb-0.5"
            style={{ color: "#F3EDDD", fontFamily: '"Cormorant Garamond", serif', fontSize: "1.2rem", lineHeight: 1.3 }}
          >
            Step into the field
          </p>
          <p
            className="italic opacity-75"
            style={{ color: "rgba(243, 237, 221, 0.66)", fontFamily: '"Cormorant Garamond", serif', fontSize: "0.92rem" }}
          >
            Join a circle with your invite code · or start your own.
          </p>
        </div>
        <ArrowRight size={14} style={{ color: "#D7BB7A" }} />
      </motion.button>
    );
  }

  // Prefer the team-specific deep link so leaders walking with James
  // land directly on the team's hub instead of the team-picker view.
  const route = `/team-alignment/${team.id}`;
  const previewRoute = `${route}?preview=1`;
  const isLeader = !!team.leader;

  return (
    <div className="mb-6">
      <motion.button
        type="button"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onClick={() => navigate(route)}
        className="w-full rounded-2xl p-5 sm:p-6 text-left flex items-center gap-4"
        style={{
          background:
            "radial-gradient(ellipse at 0% 50%, rgba(215,187,122,0.22) 0%, rgba(7,13,11,0.92) 70%)",
          border: "1px solid rgba(215,187,122,0.45)",
          boxShadow: "0 0 16px rgba(215,187,122,0.10)",
        }}
        data-testid="home-team-alignment-card"
      >
        <motion.div
          animate={{ scale: [1, 1.05, 1], opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
          style={{
            background: "rgba(215,187,122,0.16)",
            border: "1px solid rgba(215,187,122,0.45)",
          }}
        >
          <Sparkles size={14} style={{ color: "#F3EDDD" }} />
        </motion.div>

        <div className="flex-1 min-w-0">
          <p
            className="uppercase tracking-[0.32em] mb-1"
            style={{ color: "#D7BB7A", fontFamily: '"Manrope", sans-serif', fontSize: "0.56rem" }}
            data-testid="home-team-alignment-kicker"
          >
            Team Alignment
            {team.leader ? " · Leader" : " · Member"}
          </p>
          <p
            className="italic mb-0.5"
            style={{ color: "#F3EDDD", fontFamily: '"Cormorant Garamond", serif', fontSize: "1.2rem", lineHeight: 1.3 }}
            data-testid="home-team-alignment-headline"
          >
            {team.name}
          </p>
          <p
            className="italic flex items-center gap-1.5"
            style={{ color: "rgba(243, 237, 221, 0.66)", fontFamily: '"Cormorant Garamond", serif', fontSize: "0.92rem" }}
          >
            <Users size={11} style={{ color: "#D7BB7A" }} />
            {team.member_count} {team.member_count === 1 ? "member" : "members"}
            {team.default_session_time ? <> · gathers at <em style={{ color: "#D7BB7A" }}>{team.default_session_time}</em></> : null}
            {count > 1 ? <> · +{count - 1} other</> : null}
          </p>

          {/* D3.59 — Today's carried compass. Shown only when the
              member tapped "Carry one with me today" at the end of an
              immersion. Auto-expires at midnight UTC server-side. */}
          {carried?.line && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="mt-3 pt-3"
              style={{ borderTop: "1px solid rgba(215, 187, 122, 0.18)" }}
              data-testid="home-carried-line"
            >
              <p
                className="uppercase tracking-[0.32em] mb-1"
                style={{ color: "#D7BB7A", fontFamily: '"Manrope", sans-serif', fontSize: "0.5rem" }}
              >
                Carrying with you today
              </p>
              <p
                className="italic"
                style={{
                  color: "#F3EDDD",
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "1.02rem",
                  lineHeight: 1.45,
                  fontWeight: 300,
                }}
              >
                {carried.line}
              </p>
            </motion.div>
          )}
        </div>

        <ArrowRight size={14} style={{ color: "#D7BB7A" }} />
      </motion.button>

      {/* Leader-only · direct member-view link. Lets leaders walk the
          exact member journey for a live demo without going through
          the Composer first. */}
      {isLeader && (
        <motion.button
          type="button"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          onClick={(e) => { e.stopPropagation(); navigate(previewRoute); }}
          className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-full uppercase"
          style={{
            background: "transparent",
            border: "1px solid rgba(215,187,122,0.28)",
            color: "rgba(243, 237, 221, 0.78)",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.58rem",
            letterSpacing: "0.28em",
          }}
          data-testid="home-team-alignment-preview"
        >
          <Eye size={11} style={{ color: "#D7BB7A" }} />
          Preview as member
        </motion.button>
      )}
    </div>
  );
}
